// packages/studio/web/src/proposals/proposalsApi.ts
//
// Client for the changeset ("proposal") endpoints. A changeset is one entity
// with states: draft → proposed → accepted | archived. A draft is DB-only
// (its content lives in proposal-scoped drafts); proposing materialises a git
// branch + squash commit.

import { apiFetch } from "../api/client";

export type ChangesetState = "draft" | "proposed" | "accepted" | "archived";

export interface Changeset {
  readonly id: string;
  /** Per-repo sequential id (PR-style) — the changeset's URL key. */
  readonly number: number;
  readonly repoId: string;
  readonly authorUserId: string;
  readonly title: string;
  /** Commit message body — short "what & why" prose. */
  readonly description: string;
  /** Container the proposal is scoped to (package or space id; "" = root). */
  readonly target: string;
  /** Path of the attached change document, if any. null = bare proposal. */
  readonly docPath: string | null;
  readonly state: ChangesetState;
  readonly trunkBranch: string;
  readonly featureBranch: string | null;
  readonly baseOid: string | null;
  readonly headOid: string | null;
  readonly mergedOid: string | null;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface DraftRow {
  readonly path: string;
  readonly content: string;
  /** Tombstone: a deleted committed file. */
  readonly deleted: boolean;
  readonly updatedAt: number;
}

function base(slug: string, repo: string): string {
  return `/api/ws/${encodeURIComponent(slug)}/r/${encodeURIComponent(repo)}`;
}

// Encode a VFS path per-segment so the slashes stay real slashes. Using plain
// encodeURIComponent(path) turned "services/api.arch" into "services%2Fapi.arch"
// (Hono's decodeURI preserves %2F), so a nested file was stored + committed under
// a mangled root-level key (bugloop L4 H1).
function encodePath(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

// ---------------------------------------------------------------------------
// Repo container discovery (packages + spaces) — feeds the proposal target picker
// ---------------------------------------------------------------------------

/** A package or space discovered in a commit tree — a candidate proposal target. */
export interface Container {
  /** Bare tree path ("" = repo root / anonymous root package). */
  readonly path: string;
  /** Dotted canonical name, if the container is named. */
  readonly name?: string;
  /** Packages only: true = manifest-less (implicit/anonymous) package. */
  readonly implicit?: boolean;
}

export interface ContainerDiscovery {
  readonly packages: Container[];
  readonly spaces: Container[];
}

/** Discover packages + spaces at a ref (default trunk) for the target picker. */
export async function discoverContainers(
  slug: string,
  repo: string,
  ref: string,
): Promise<ContainerDiscovery> {
  const { packages, spaces } = await apiFetch<{ packages: Container[]; spaces: Container[] }>(
    `${base(slug, repo)}/packages/${encodeURIComponent(ref)}`,
  );
  return { packages, spaces };
}

// ---------------------------------------------------------------------------
// Changeset CRUD + lifecycle
// ---------------------------------------------------------------------------

/** Get-or-create the single unnamed draft for this user+repo+trunk. */
export async function getOrCreateUnnamedDraft(slug: string, repo: string): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(`${base(slug, repo)}/proposals`, {
    method: "POST",
    body: {},
  });
  return proposal;
}

export async function getChangeset(slug: string, repo: string, id: string): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}`,
  );
  return proposal;
}

/** Fetch a changeset by its per-repo number (the URL key). */
export async function getChangesetByNumber(
  slug: string,
  repo: string,
  number: number,
): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/n/${number}`,
  );
  return proposal;
}

export async function listChangesets(
  slug: string,
  repo: string,
  opts: { state?: ChangesetState; mine?: boolean } = {},
): Promise<Changeset[]> {
  const params = new URLSearchParams();
  if (opts.state) params.set("state", opts.state);
  if (opts.mine) params.set("mine", "1");
  const qs = params.toString();
  const { proposals } = await apiFetch<{ proposals: Changeset[] }>(
    `${base(slug, repo)}/proposals${qs ? `?${qs}` : ""}`,
  );
  return proposals;
}

/** Patch a changeset's authored fields (title / description / target / doc pointer). */
export async function patchChangeset(
  slug: string,
  repo: string,
  id: string,
  fields: { title?: string; description?: string; target?: string; docPath?: string | null },
): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}`,
    { method: "PATCH", body: fields },
  );
  return proposal;
}

/** Name / rename a changeset (thin wrapper over patchChangeset). */
export async function renameChangeset(
  slug: string,
  repo: string,
  id: string,
  title: string,
): Promise<Changeset> {
  return patchChangeset(slug, repo, id, { title });
}

/** draft → proposed (branch + squash commit + open). */
export async function proposeChangeset(
  slug: string,
  repo: string,
  id: string,
  fields?: { title?: string; description?: string },
): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/propose`,
    { method: "POST", body: fields ?? {} },
  );
  return proposal;
}

/** proposed: commit the current drafts as a new commit on the branch. */
export async function updateProposal(slug: string, repo: string, id: string): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/update`,
    { method: "POST", body: {} },
  );
  return proposal;
}

/** proposed: merge the trunk into the changeset branch (catch up to trunk). */
export async function updateFromTrunk(slug: string, repo: string, id: string): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/update-from-trunk`,
    { method: "POST", body: {} },
  );
  return proposal;
}

/** proposed → accepted (squash → trunk, single commit). */
export async function acceptProposal(slug: string, repo: string, id: string): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/merge`,
    { method: "POST", body: {} },
  );
  return proposal;
}

/** → archived (drop branch, keep diff in DB). */
export async function archiveChangeset(slug: string, repo: string, id: string): Promise<Changeset> {
  const { proposal } = await apiFetch<{ proposal: Changeset }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/archive`,
    { method: "POST", body: {} },
  );
  return proposal;
}

// ---------------------------------------------------------------------------
// Proposal-scoped drafts (autosave)
// ---------------------------------------------------------------------------

/** Fetch a changeset's drafts. Best-effort: [] on failure. */
export async function fetchDrafts(slug: string, repo: string, changesetId: string): Promise<DraftRow[]> {
  try {
    const { drafts } = await apiFetch<{ drafts: DraftRow[] }>(
      `${base(slug, repo)}/proposals/${encodeURIComponent(changesetId)}/drafts`,
    );
    return drafts;
  } catch {
    return [];
  }
}

// The autosave calls below MUST propagate failures — useStudioWorkspace wraps
// each in track() and reports rejections via onSaveError. Swallowing here would
// make that "no silent data loss" surfacing dead code (bugloop L1 #1).

/** Upsert one draft (autosave). Rejects on failure so the host can surface it. */
export async function saveDraft(
  slug: string,
  repo: string,
  changesetId: string,
  path: string,
  content: string,
): Promise<void> {
  await apiFetch(
    `${base(slug, repo)}/proposals/${encodeURIComponent(changesetId)}/drafts/${encodePath(path)}`,
    { method: "PUT", body: { content } },
  );
}

/** Tombstone a committed file (delete it in this changeset). Rejects on failure. */
export async function markDeleted(
  slug: string,
  repo: string,
  changesetId: string,
  path: string,
): Promise<void> {
  await apiFetch(
    `${base(slug, repo)}/proposals/${encodeURIComponent(changesetId)}/drafts/${encodePath(path)}`,
    { method: "PUT", body: { deleted: true } },
  );
}

/** Delete one draft (file reverted). Rejects on failure. */
export async function deleteDraft(
  slug: string,
  repo: string,
  changesetId: string,
  path: string,
): Promise<void> {
  await apiFetch(
    `${base(slug, repo)}/proposals/${encodeURIComponent(changesetId)}/drafts/${encodePath(path)}`,
    { method: "DELETE" },
  );
}

// ---------------------------------------------------------------------------
// Review comments
// ---------------------------------------------------------------------------

export type AnchorKind = "node" | "line";

export type CommentAnchor =
  | { kind: "node"; nodeId: string; nodeKind: string }
  | { kind: "line"; path: string; line: number; commitOid: string; side?: "base" | "head" };

export interface ReactionSummary {
  readonly emoji: string;
  readonly count: number;
  readonly mine: boolean;
}

export interface ReviewComment {
  readonly id: string;
  readonly body: string;
  readonly authorUserId: string;
  readonly authorName: string | null;
  readonly createdAt: string | number;
  readonly parentCommentId: string | null;
  readonly anchorKind: AnchorKind | null;
  readonly nodeId: string | null;
  readonly nodeKind: string | null;
  readonly path: string | null;
  readonly line: number | null;
  readonly side: string | null;
  readonly commitOid: string | null;
  readonly anchorSnapshot: string | null;
  readonly outdated: boolean;
  readonly resolved: boolean;
  readonly resolvedBy: string | null;
  readonly resolvedAt: string | number | null;
  readonly reactions: ReactionSummary[];
  readonly replies: ReviewComment[];
}

export interface ReviewThreads {
  /** Root comments oldest→newest, each with its replies. */
  readonly comments: ReviewComment[];
  /** Proposal-level reaction summary. */
  readonly reactions: ReactionSummary[];
}

/** List a proposal's review threads (roots + replies) and proposal reactions. */
export async function listComments(slug: string, repo: string, id: string): Promise<ReviewThreads> {
  return apiFetch<ReviewThreads>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/comments`,
  );
}

/** Post a review comment, optionally as a reply and/or anchored to a node/line. */
export async function postComment(
  slug: string,
  repo: string,
  id: string,
  body: string,
  opts?: { parentCommentId?: string; anchor?: CommentAnchor },
): Promise<ReviewComment> {
  const { comment } = await apiFetch<{ comment: ReviewComment }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/comments`,
    {
      method: "POST",
      body: {
        body,
        ...(opts?.parentCommentId ? { parentCommentId: opts.parentCommentId } : {}),
        ...(opts?.anchor ? { anchor: opts.anchor } : {}),
      },
    },
  );
  return comment;
}

/** Resolve / unresolve a thread (root comment). */
export async function resolveComment(
  slug: string,
  repo: string,
  id: string,
  commentId: string,
  resolved: boolean,
): Promise<void> {
  await apiFetch(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}/resolve`,
    { method: "POST", body: { resolved } },
  );
}

/** Toggle the caller's emoji reaction on a comment/reply. */
export async function toggleCommentReaction(
  slug: string,
  repo: string,
  id: string,
  commentId: string,
  emoji: string,
): Promise<ReactionSummary[]> {
  const { reactions } = await apiFetch<{ reactions: ReactionSummary[] }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}/reactions`,
    { method: "POST", body: { emoji } },
  );
  return reactions;
}

/** Toggle the caller's emoji reaction on the proposal itself. */
export async function toggleProposalReaction(
  slug: string,
  repo: string,
  id: string,
  emoji: string,
): Promise<ReactionSummary[]> {
  const { reactions } = await apiFetch<{ reactions: ReactionSummary[] }>(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/reactions`,
    { method: "POST", body: { emoji } },
  );
  return reactions;
}

/** Delete one of your own review comments (cascades replies + reactions). */
export async function deleteComment(
  slug: string,
  repo: string,
  id: string,
  commentId: string,
): Promise<void> {
  await apiFetch(
    `${base(slug, repo)}/proposals/${encodeURIComponent(id)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE" },
  );
}

/** GitHub reaction set (must match the server allowlist). */
export const REACTION_EMOJI = ["👍", "👎", "😄", "🎉", "😕", "❤️", "🚀", "👀"] as const;
